import React, { useMemo } from 'react';

import { AgGridReact } from 'ag-grid-react';
import { EyeIcon, PencilIcon, PlusIcon } from 'lucide-react';
import { Button } from 'primereact/button';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { DonorSchedulingActions } from '@/app/slices/donorScheduling';
import { PartnershipsActions } from '@/app/slices/partnerships';
import { Button as BaseButton } from '@/components/ui/button';
import { accountStatusOptions } from '@/constants';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useDonorSchedulePastDueStatus } from '@/hooks/usePastDueStatus';
import { DonorSchedule } from '@/types.gql';

import styles from './EditButton.module.css';

export const EditButton = ({
  gridRef,
  variant = 'prime',
}: {
  gridRef: React.RefObject<AgGridReact<DonorSchedule>>;
  variant?: 'prime' | 'base-ui';
}) => {
  const [params, setParams] = useSearchParams();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const isNucleusLinkEnabled = useFeatureFlag('Fert_Nucleus_Hyperlinks');
  const { selectedDonorSchedules } = useAppSelector(
    (state) => state.donorScheduling,
  );
  const { workingDonorSchedule } = useAppSelector(
    (state) => state.donorScheduling,
  );

  const closedNoNewBusinessValue =
    accountStatusOptions.find((opt) => opt.label === 'Closed - No New Business')
      ?.value ??
    accountStatusOptions.find((opt) => opt.value === 100000003)?.value ??
    null;

  const selectedCount = gridRef?.current?.api?.getSelectedNodes().length ?? 0;

  const isLegacyPastDue = useMemo(() => {
    return !!(
      workingDonorSchedule?.owner?.aging180 ||
      workingDonorSchedule?.owner?.aging365 ||
      workingDonorSchedule?.owner?.accountStatus === closedNoNewBusinessValue
    );
  }, [
    workingDonorSchedule?.owner?.aging180,
    workingDonorSchedule?.owner?.aging365,
    workingDonorSchedule?.owner?.accountStatus,
  ]);

  const isBillingAccountPastDueEnabled = useFeatureFlag(
    'DS_Past_Due_Billing_Account',
  );
  const { hasOver90Days: isBillingAccountPastDue } =
    useDonorSchedulePastDueStatus(workingDonorSchedule);
  const isPastDue = isBillingAccountPastDueEnabled
    ? isBillingAccountPastDue
    : isLegacyPastDue;

  const label = useMemo(() => {
    if (selectedCount > 0 && isPastDue) {
      return t('common.viewSelected');
    }

    if (selectedCount > 0) {
      return t('common.editSelected');
    }

    return t('donorScheduling.newDonorSchedule');
  }, [selectedCount, isPastDue, t]);

  const onClick = () => {
    const newParams = new URLSearchParams(params);
    newParams.set('edit', 'true');
    const gridSelectedCount =
      gridRef?.current?.api?.getSelectedNodes().length ?? 0;
    if (isNucleusLinkEnabled && gridSelectedCount === 0) {
      // New schedule: never reuse a previously opened schedule left in redux.
      dispatch({
        type: DonorSchedulingActions.RESET_SELECTED_DONOR_SCHEDULING,
      });
      dispatch({ type: DonorSchedulingActions.RESET_WORKING_DONOR_SCHEDULE });
      dispatch({ type: PartnershipsActions.RESET_PARTNERSHIP });
      newParams.set('id', '-1');
    } else {
      newParams.set(
        'id',
        workingDonorSchedule.id ? workingDonorSchedule.id : '-1',
      );
    }
    setParams(newParams);
  };

  if (variant === 'base-ui') {
    const Icon = isPastDue
      ? EyeIcon
      : selectedCount > 0
        ? PencilIcon
        : PlusIcon;
    return (
      <BaseButton onClick={onClick}>
        <Icon />
        {label}
        {selectedCount > 1 && (
          <span className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold text-white">
            {selectedCount}
          </span>
        )}
      </BaseButton>
    );
  }

  return (
    <Button
      className={styles['edit-button']}
      label={label}
      icon={`pi pi-${isPastDue ? 'eye' : selectedCount > 0 ? 'pencil' : 'plus'}`}
      badge={selectedCount > 1 ? selectedCount.toString() : undefined}
      badgeClassName="p-badge-danger"
      onClick={onClick}
    />
  );
};
